import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, Save } from "lucide-react";
import QuestionUploadPanel from "@/components/QuestionUploadPanel";
import QuestionEditor from "@/components/QuestionEditor";
import { api } from "@/lib/api";
import type { ParsedQuestionDocResult, ParsedQuestionDraft } from "@/types/questions";

export default function AdminUploadPage() {
  const nav = useNavigate();
  const [parsed, setParsed] = useState<ParsedQuestionDocResult | null>(null);
  const [drafts, setDrafts] = useState<ParsedQuestionDraft[]>([]);
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onParsed = useCallback((res: ParsedQuestionDocResult) => {
    setParsed(res);
    setDrafts(res.questions);
    setTitle(res.title);
  }, []);

  function updateDraft(idx: number, d: ParsedQuestionDraft) {
    setDrafts((prev) => {
      const next = [...prev];
      next[idx] = { ...d, valid: !!d.correct_option && !!d.option_a && !!d.option_b && !!d.option_c && !!d.option_d && !!d.question_text };
      return next;
    });
  }

  function removeDraft(idx: number) {
    setDrafts((prev) => prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, position: i + 1 })));
  }

  async function publish() {
    setBusy(true); setErr(null);
    try {
      const ready = drafts.filter((d) => d.valid);
      const result = await api.publishQuestionSet({
        title,
        description: description || undefined,
        questions: ready,
      });
      sessionStorage.setItem("weldquest.lastPublished", JSON.stringify(result));
      nav("/admin/questions");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not publish.");
    } finally {
      setBusy(false);
    }
  }

  const validCount = drafts.filter((d) => d.valid).length;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl text-white">Upload question bank</h1>
        <p className="text-steel-300">Parse a Word document, review every question, then publish.</p>
      </header>

      {!parsed && <QuestionUploadPanel onParsed={onParsed} />}

      {parsed && (
        <>
          <section className="card-dark p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="pill-ok">{validCount} ready</span>
                <span className="pill-warn">{drafts.length - validCount} need review</span>
              </div>
              <button onClick={() => { setParsed(null); setDrafts([]); }} className="btn-outline">
                Replace file
              </button>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" />
            </div>
          </section>

          <section className="space-y-3">
            {drafts.map((d, i) => (
              <QuestionEditor
                key={i}
                initial={d}
                onChange={(nd) => updateDraft(i, nd)}
                onRemove={() => removeDraft(i)}
              />
            ))}
            {drafts.length === 0 && <div className="text-steel-300 text-sm">No questions remain.</div>}
          </section>

          {err && (
            <div className="pill-bad px-3 py-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {err}
            </div>
          )}

          <div className="sticky bottom-3 flex justify-end">
            <button onClick={publish} className="btn-primary" disabled={busy || validCount === 0 || !title.trim()}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {busy ? "Publishing…" : `Publish ${validCount} question${validCount === 1 ? "" : "s"}`}
              {validCount > 0 && <CheckCircle2 className="w-4 h-4 ml-1" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
